import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import FiltersBarCst from "@components/FiltersBarCst";
import { FiltersBarValue } from "@components/FiltersBarCst/types";
import { FiltersModalProps } from "./types";

const FiltersModalCst: React.FC<FiltersModalProps> = ({
  open,
  onClose,
  pageId,
  value,
  onApply,
  excludeKeys,
  filterVisibility,
  title,
}) => {
  const { t } = useTranslation("catalog-v2");
  const theme = useTheme();

  const [draft, setDraft] = useState<FiltersBarValue>(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const handleReset = () => setDraft({});

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title ?? t("filters-modal.title")}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "20px 24px 16px",
          "& .MuiGrid-container": { marginTop: 0 },
          "& .MuiGrid-item": { paddingTop: "16px !important" },
        }}
      >
        <FiltersBarCst
          pageId={pageId}
          value={draft}
          onChange={setDraft}
          excludeKeys={excludeKeys}
          filterVisibility={filterVisibility}
        />
      </DialogContent>

      <DialogActions
        sx={{
          padding: "16px 24px 20px",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleReset}
          variant="text"
          sx={{
            textTransform: "none",
            color: theme.palette.primary.main,
            fontWeight: 600,
            textDecoration: "underline",
          }}
        >
          {t("filters-modal.reset")}
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {t("filters-modal.apply")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiltersModalCst;
